import { z } from 'zod';

const uuid = z.string().uuid();

/**
 * What a consent covers (ADR-0011): school operations, sensitive data, AI processing; `notice`
 * is the privacy notice shown at consent time, never "granted".
 */
export const consentPurposeSchema = z.enum(['general', 'sensitive', 'ai', 'notice']);

/**
 * Purposes a family actually grants.
 */
export const grantablePurposeSchema = z.enum(['general', 'sensitive', 'ai']);

/**
 * How the authorization was captured.
 */
export const consentChannelSchema = z.enum(['app', 'paper', 'onboarding']);

/**
 * Publishes a new version of a text for the school.
 */
export const publishConsentTextInputSchema = z.object({
  title: z.string().trim().min(3).max(200),
  body: z.string().trim().min(50).max(20_000),
});

/**
 * Captures one or more consents for a student. The guardian is a user of the school when the
 * family authorized from the app or the guardian was already created; on paper, the name and
 * document of the signer are recorded and the scanned form's upload key is the evidence.
 */
export const captureConsentInputSchema = z
  .object({
    purposes: z.array(grantablePurposeSchema).min(1).max(3),
    channel: consentChannelSchema,
    authorizedByUserId: uuid.optional(),
    authorizedByName: z.string().trim().min(3).max(160).optional(),
    authorizedByDocument: z.string().trim().min(3).max(48).optional(),
    evidenceKey: z.string().trim().max(500).optional(),
    grantedAt: z.string().datetime({ offset: true }).optional(),
  })
  .refine((value) => value.authorizedByUserId || value.authorizedByName, {
    message: 'Who authorized is required: a guardian user or a name',
    path: ['authorizedByUserId'],
  })
  .refine((value) => value.channel !== 'paper' || value.evidenceKey, {
    message: 'A paper consent needs the uploaded form as evidence',
    path: ['evidenceKey'],
  });

/**
 * Revokes a consent from a date, with the reason the family gave.
 */
export const revokeConsentInputSchema = z.object({
  reason: z.string().trim().max(300).optional(),
});

/**
 * One purpose's text as the apps show it.
 */
export type ConsentPurpose = z.infer<typeof consentPurposeSchema>;
/**
 * A purpose a family grants.
 */
export type GrantablePurpose = z.infer<typeof grantablePurposeSchema>;
/**
 * Where the authorization came from.
 */
export type ConsentChannel = z.infer<typeof consentChannelSchema>;
/**
 * Body of `PUT /consents/texts/{purpose}`.
 */
export type PublishConsentTextInput = z.input<typeof publishConsentTextInputSchema>;
/**
 * Body of `POST /students/{id}/consents`.
 */
export type CaptureConsentInput = z.input<typeof captureConsentInputSchema>;
/**
 * Body of `POST /consents/{id}/revoke`.
 */
export type RevokeConsentInput = z.input<typeof revokeConsentInputSchema>;

/**
 * A consent text version.
 */
export type ConsentTextView = {
  id: string;
  purpose: ConsentPurpose;
  version: number;
  title: string;
  body: string;
  isDefault: boolean;
  publishedAt: string;
};

/**
 * One consent record with its evidence.
 */
export type ConsentView = {
  id: string;
  studentUserId: string;
  purpose: GrantablePurpose;
  channel: ConsentChannel;
  textVersion: number;
  textTitle: string;
  authorizedBy: {
    userId: string | null;
    name: string | null;
    document: string | null;
  };
  evidenceKey: string | null;
  grantedAt: string;
  revokedAt: string | null;
  revokedReason: string | null;
  active: boolean;
};

/**
 * The state of a student's consents per purpose, for screens that gate features.
 */
export type ConsentStatus = Record<GrantablePurpose, boolean>;

/**
 * The per-family export: every consent of every student linked to a guardian.
 */
export type FamilyConsentExport = {
  guardian: {
    id: string;
    givenName: string | null;
    familyName: string | null;
    email: string | null;
  };
  students: {
    id: string;
    givenName: string | null;
    familyName: string | null;
    consents: ConsentView[];
  }[];
  exportedAt: string;
};
